import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Box from '@mui/material/Box';
import {
  useRotationDrawerState,
  useSeriesDrawerState,
  useShiftsFilter,
  useRotationsListStore,
} from '@libs/store/shifts';
import { useRefreshSyncStore } from '@libs/store/refreshSync';
import { useLocationStore } from '@libs/store/locations';
import { outputShiftDataToConsole } from '@pages/schedule/helpers';
import {
  DataGridProgress,
  DEFAULT_PAGE_SIZE,
  EmptyListPlaceholder,
  GridSortItem,
  GridSortModel,
  isDateSeparator,
  PAGE_SIZE_OPTIONS,
  separatorClass,
} from '@molecules/dataDisplay';
import { DataGrid } from '@molecules/dataDisplay';
import { ShiftsIcon } from '@atoms/icons';
import { useRecurrencesListColumns } from './recurrencesColumns';
import { TIMEZONE_FALLBACK } from '@libs/config';
import dayjs from 'dayjs';
import { DateRangeFilter } from '@libs/store/filters';

export function SeriesNRotationsTable() {
  const { refreshTrigger } = useRefreshSyncStore();
  const {
    data,
    loadSeriesNRotations,
    loading: rotationsListLoading,
    initiated: rotationsListInitiated,
    total,
    page,
    perPage,
  } = useRotationsListStore();
  const { activeFilters, hasFilter, getFilter } = useShiftsFilter();
  const { currentLocationID, current: currentLocation } = useLocationStore();
  const [openSeriesDrawer] = useSeriesDrawerState();
  const [openRotationDrawer] = useRotationDrawerState();
  const { sortModel, setSortModel, sortFilter } = useSortHandler();

  const loading = !rotationsListInitiated || rotationsListLoading;

  useEffect(() => {
    if (currentLocationID) {
      const startDate = getFilter('startDate');
      if (!hasFilter('startDate') || !(startDate as DateRangeFilter)?.value?.from) {
        // If startDate filter is not set, we set it to the current date
        activeFilters.startDate = {
          key: 'startDate',
          value: {
            from: dayjs().startOf('day'),
            timezone: currentLocation?.timezone || TIMEZONE_FALLBACK, // Default timezone, can be adjusted
          },
        };
      }
      loadSeriesNRotations(1, DEFAULT_PAGE_SIZE, {
        ...activeFilters,
        locations: { key: 'locations', value: [currentLocationID] },
        orderBy: sortFilter,
      });
    }
  }, [loadSeriesNRotations, currentLocationID, activeFilters, refreshTrigger, sortFilter]);

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <DataGridProgress loading={loading} />
      <DataGrid
        rows={data}
        rowHeight={64}
        columns={useRecurrencesListColumns()}
        onSortModelChange={setSortModel}
        sortModel={sortModel}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: DEFAULT_PAGE_SIZE,
            },
          },
        }}
        paginationModel={{
          pageSize: perPage,
          page: page,
        }}
        rowCount={total}
        onPaginationModelChange={(params) => {
          if (!rotationsListLoading) {
            loadSeriesNRotations(params.page, params.pageSize, activeFilters);
          }
        }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disableRowSelectionOnClick
        getRowHeight={({ model }) => {
          if (model.type === 'separator') {
            return 44;
          }

          return null;
        }}
        getRowClassName={(params) => {
          if (isDateSeparator(params.row)) {
            return separatorClass;
          }
          return '';
        }}
        noRowsOverlay={
          <EmptyListPlaceholder
            icon={<ShiftsIcon active />}
            title={<FormattedMessage id="shifts.table.noRows.title" />}
            subtitle={<FormattedMessage id="shifts.table.noRows.subtitle" />}
          />
        }
        onRowClick={(params, evt) => {
          if (evt.ctrlKey) {
            outputShiftDataToConsole(params.row);
          }
          if (params.row.recurrence === 'rotation') {
            openRotationDrawer(`${params.id}`);
          } else {
            openSeriesDrawer(`${params.id}`);
          }
        }}
      />
    </Box>
  );
}

/**
 * @TODO: Split out series and rotations list and shifts list stores.
 * And move this section into RecurrencesFilterStore
 */
function useSortHandler() {
  const [sortModel, setSortModel] = useState<GridSortModel>([defaultDateSortModel]);
  const sortFilter = useMemo(
    () => ({
      key: 'orderBy',
      value: sortModel.map((p) => `${propToFilterMap[p.field] ?? p.field}_${p.sort}`),
    }),
    [sortModel],
  );

  return { sortModel, sortFilter, setSortModel };
}

const defaultDateSortModel: GridSortItem = {
  field: 'shiftStart',
  sort: 'asc',
};

const propToFilterMap: Record<string, string> = {
  shiftStart: 'startDate',
  status: 'statuses',
};
